import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import clsx from 'clsx';

const SIZE = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
};

const FOCUSABLE =
  'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';

function trapFocus(e, container) {
  if (!container) return;
  const focusables = container.querySelectorAll(FOCUSABLE);
  if (!focusables.length) return;
  const first = focusables[0];
  const last = focusables[focusables.length - 1];
  if (e.shiftKey && document.activeElement === first) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && document.activeElement === last) {
    e.preventDefault();
    first.focus();
  }
}

export default function Dialog({
  open,
  onClose,
  title,
  description,
  icon: Icon,
  size = 'md',
  children,
  footer,
  closeOnOverlay = true,
}) {
  const panelRef = useRef(null);
  const previousFocus = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    previousFocus.current = document.activeElement;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
      if (e.key === 'Tab') trapFocus(e, panelRef.current);
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    queueMicrotask(() => {
      const first = panelRef.current?.querySelector(FOCUSABLE);
      (first || panelRef.current)?.focus?.();
    });
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      previousFocus.current?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'dialog-title' : undefined}
    >
      <button
        type="button"
        aria-label="Close dialog"
        tabIndex={-1}
        className="absolute inset-0 bg-ink-900/60 backdrop-blur-sm cursor-default"
        onClick={closeOnOverlay ? onClose : undefined}
      />
      <div
        ref={panelRef}
        tabIndex={-1}
        className={clsx(
          'relative w-full bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl outline-none',
          'max-h-[92vh] flex flex-col',
          SIZE[size] || SIZE.md,
        )}
      >
        {(title || Icon) && (
          <div className="flex items-start gap-3 px-5 sm:px-6 pt-5 pb-3 border-b border-gray-50">
            {Icon && (
              <span className="flex-none mt-0.5 h-9 w-9 rounded-xl bg-brand-50 text-brand-600 flex items-center justify-center">
                <Icon className="w-4 h-4" />
              </span>
            )}
            <div className="flex-1 min-w-0">
              {title && (
                <h2
                  id="dialog-title"
                  className="text-base font-semibold text-ink-900 truncate"
                >
                  {title}
                </h2>
              )}
              {description && (
                <p className="text-sm text-ink-400 mt-0.5">{description}</p>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="btn-icon flex-none -mr-2 -mt-1"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        <div className="px-5 sm:px-6 py-4 overflow-y-auto scroll-soft flex-1">
          {children}
        </div>
        {footer && (
          <div className="px-5 sm:px-6 py-3 border-t border-gray-50 flex items-center justify-end gap-2 flex-wrap">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
